import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Link, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import type React from 'react';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { MonetizableEnum } from '@repo/supergraph/marketplace/enums';
import type { MarketplaceEntityTypeWithDriversAndCtaOverride } from '@repo/supergraph/marketplace/types';
import { CtaButton } from '@repo/ui/CtaButton';
import { DisclosureTooltip } from '@repo/ui/DisclosureTooltip';
import { BaseLayout as StarRating } from '@repo/ui/StarRating/BaseLayout';

interface CrossSellCardProps {
  elementPosition: number;
  marketplaceEntity: MarketplaceEntityTypeWithDriversAndCtaOverride;
  numProductCards: number;
  sectionName: string;
}

export const CrossSellCard: React.FC<CrossSellCardProps> = ({
  elementPosition,
  marketplaceEntity,
  numProductCards,
  sectionName,
}) => {
  const { displayName, image, cta, drivers } = marketplaceEntity;
  return (
    <Grid size={{ xs: 12, sm: 12 / numProductCards }}>
      <AnalyticsSkuViewed
        source="marketplace"
        name={marketplaceEntity?.name}
        productType={marketplaceEntity?.productType}
        level="offer"
        institutionId={marketplaceEntity?.institution?.id}
        institutionName={marketplaceEntity?.institution?.name}
        productId={marketplaceEntity?.product?.id}
        productName={marketplaceEntity?.product?.name}
        offerId={marketplaceEntity?.id}
        offerName={marketplaceEntity?.name}
        monetizable={marketplaceEntity?.monetizable}
        impressionSection={sectionName}
        impressionPosition={elementPosition}
      >
        <div style={{ height: '100%' }}>
          <Box
            sx={{
              paddingX: 3,
              paddingBottom: 2,
              marginBottom: 2,
              border: '1px solid neutral.lighter',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 8px 0 rgba(100,102,106,.1),0 1px 4px 0 rgba(100,102,106,.36)',
              flex: 1,
              height: '100%',
            }}
          >
            <Grid container sx={{ display: 'flex', paddingTop: 3 }}>
              <Grid
                size={{ xs: 9 }}
                sx={{
                  paddingRight: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="textBold"
                  component="h2"
                  sx={{
                    marginBottom: 1,
                    flexBasis: '48px',
                    flexGrow: 1,
                  }}
                >
                  {displayName}
                </Typography>
                <StarRating
                  starRating={marketplaceEntity.starRating}
                  starRatingTooltip={marketplaceEntity.starRatingTooltip}
                  reviewLink={marketplaceEntity.reviewLink}
                  smallRatingText
                  wrapRatingText
                />
              </Grid>
              <Grid size={{ xs: 3 }} sx={{ maxHeight: '62px', maxWidth: '75px' }}>
                {image?.sourceMedium && (
                  <Image
                    src={image?.sourceMedium}
                    alt={image?.alt || 'Card logo'}
                    width={75}
                    height={62}
                    style={{ height: 'auto', width: '100%' }}
                  />
                )}
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', flexGrow: 1 }}>
              {drivers.map(driver => {
                const { id, description, label, value, valueTooltip } = driver;
                return (
                  <Box
                    key={label}
                    sx={{
                      marginTop: 2,
                      paddingRight: 3,
                      flexBasis: '50%',
                      overflowWrap: 'break-word',
                    }}
                  >
                    <Typography variant="textSmall" sx={{ color: 'text.secondary' }}>
                      {label}
                    </Typography>
                    <Box sx={{ display: 'flex' }}>
                      <Typography data-test-id={`${id}-value`} variant="bodyExtraLarge">
                        {value || 'N/A'}
                      </Typography>
                      {valueTooltip && (
                        <DisclosureTooltip
                          placement="top"
                          trigger={
                            <Link
                              aria-label={`${label} info`}
                              component="button"
                              color="text.primary"
                            >
                              <HelpOutlineIcon />
                            </Link>
                          }
                        >
                          <Typography variant="textSmall">{valueTooltip}</Typography>
                        </DisclosureTooltip>
                      )}
                    </Box>
                    <Box>
                      {description && (
                        <Typography
                          component="p"
                          variant="textSmall"
                          sx={{
                            lineHeight: 1.5,
                            color: 'neutral.darker',
                          }}
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ marginTop: 2, flexBasis: '100px' }}>
              <CtaButton
                cta={cta}
                analyticsPayload={{
                  index: elementPosition,
                  institutionId: marketplaceEntity?.institution?.id,
                  monetizable: marketplaceEntity?.monetizable || MonetizableEnum.no,
                  offerId: marketplaceEntity?.id,
                  productId: marketplaceEntity?.product?.id,
                  productType: marketplaceEntity.productType,
                }}
                federallyInsuredAnnotation={marketplaceEntity.federallyInsuredAnnotation}
              />
            </Box>
          </Box>
        </div>
      </AnalyticsSkuViewed>
    </Grid>
  );
};
